import { useMemo } from 'react'

import { useCubeReportPageFiltersContext } from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS } from 'cube/constants/QueryConstants'
import {
  RoutingSankeyDrilldownBase,
  RoutingSankeyExportDrilldownBase,
} from 'cube/queries'

import { type CubeQuery, CubeQueryBuilder } from '@driftt/dds.reports'

import { MEASURE_FILTER_MAP } from 'components/routing/RoutingSankey/RoutingSankey.constants'
import useTimePeriod from 'hooks/useTimePeriod'

type RoutingReportDrillDownQueryResponse = {
  drillDownCubeQuery: CubeQuery
  exportCubeQuery: CubeQuery
}

/***
 * Hook to handle formatting drill down cube queries for the routing report.
 *
 * @returns RoutingReportDrillDownQueryResponse
 */
export const useRoutingReportDrillDownQuery = (
  filterMeasure: string
): RoutingReportDrillDownQueryResponse => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const measureFilters = useMemo(
    () => MEASURE_FILTER_MAP[filterMeasure] || [],
    [filterMeasure]
  )

  const drillDownCubeQuery = useMemo(() => {
    return new CubeQueryBuilder({
      query: RoutingSankeyDrilldownBase,
    })
      .withName(`DrillDown-${filterMeasure}`)
      .withQueryName(`DrillDown-${filterMeasure}`)
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      )
      .withFilters([...measureFilters, ...pageFilters])
      .build()
  }, [filterMeasure, measureFilters, timePeriod, pageFilters])

  const exportCubeQuery = useMemo(() => {
    return new CubeQueryBuilder({
      query: RoutingSankeyExportDrilldownBase,
    })
      .withName(`DrillDown-${filterMeasure}`)
      .withQueryName(`DrillDown-${filterMeasure}`)
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      )
      .withFilters([...measureFilters, ...pageFilters])
      .build()
  }, [filterMeasure, measureFilters, timePeriod, pageFilters])

  return useMemo(
    () => ({
      drillDownCubeQuery,
      exportCubeQuery,
    }),
    [drillDownCubeQuery, exportCubeQuery]
  )
}

export default useRoutingReportDrillDownQuery
